/* eslint-disable react/require-default-props */
import {
  alpha,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import {
  NumericFormat,
  PatternFormat,
  PatternFormatProps,
} from 'react-number-format';

import { cleanValue, convertToFloatNumber } from '../../helpers';
import { textFieldDefaultStyle } from '../UI/Typography';

interface CustomTextFieldProps {
  id: string;
  label: string;
  value: string;
  setValue: (value: string) => void;
  disabled?: boolean;
  maxLength?: number;
  required?: boolean;
  onChange?: (value: boolean) => void;
}

export function CustomTextField({
  id,
  label,
  value,
  setValue,
  disabled,
  maxLength,
  required,
  onChange,
}: CustomTextFieldProps): JSX.Element {
  return (
    <TextField
      id={id}
      label={label}
      disabled={disabled}
      required={required}
      value={value || ''}
      inputProps={{ maxLength }}
      onChange={(e) => {
        setValue(e.target.value);
        if (onChange) {
          onChange(true);
        }
      }}
      sx={textFieldDefaultStyle}
    />
  );
}

interface SelectTextFieldProps {
  id: string;
  label: string;
  value: number;
  setValue: (value: number) => void;
  selectOptions: { value: number; label: string }[];
  disable?: boolean;
  allSelectable?: boolean;
  required?: boolean;
}

export function SelectTextField({
  id,
  label,
  value,
  setValue,
  selectOptions,
  disable,
  allSelectable = true,
  required,
}: SelectTextFieldProps): JSX.Element {
  return (
    <TextField
      id={id}
      disabled={disable || false}
      required={required}
      select
      label={label}
      value={value || ''}
      onChange={(e) => {
        setValue(Number(e.target.value));
      }}
      sx={textFieldDefaultStyle}
    >
      {selectOptions.map((option, index) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={!allSelectable && index === 0}
          sx={(theme) => ({
            '&:hover': {
              backgroundColor: '#EDEDED',
            },
            '&.Mui-disabled': {
              opacity: 0.7,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.common.grey, 0.3),
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.grey, 0.3),
              },
            },
          })}
        >
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

interface SelectTextFieldStringProps {
  id: string;
  label: string;
  value: string;
  setValue: (value: string) => void;
  selectOptions: { value: string; label: string }[];
  disable?: boolean;
  required?: boolean;
}

export function SelectTextFieldString({
  id,
  label,
  value,
  setValue,
  selectOptions,
  disable,
  required,
}: SelectTextFieldStringProps): JSX.Element {
  return (
    <TextField
      id={id}
      disabled={disable || false}
      required={required}
      select
      label={label}
      value={value || ''}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      sx={textFieldDefaultStyle}
    >
      {selectOptions.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.value === 'SELECT'}
          sx={(theme) => ({
            '&:hover': {
              backgroundColor: '#EDEDED',
            },
            '&.Mui-disabled': {
              opacity: 0.7,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.common.grey, 0.3),
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.grey, 0.3),
              },
            },
          })}
        >
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

interface MultilineTextFieldProps {
  id: string;
  label: string;
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
}

export function MultilineTextField({
  id,
  label,
  value,
  setValue,
  placeholder,
  disabled,
  maxLength,
}: MultilineTextFieldProps): JSX.Element {
  return (
    <TextField
      id={id}
      disabled={disabled || false}
      multiline
      rows={6}
      label={label}
      placeholder={placeholder}
      inputProps={{ maxLength }}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      sx={textFieldDefaultStyle}
    />
  );
}

interface NumericTextFieldProps {
  id: string;
  label: ReactNode;
  suffix: string;
  decimalSeparator?: string;
  decimalScale?: number;
  maxLength: number;
  value: number;
  setValue: (value: number) => void;
  disable?: boolean;
  error?: boolean;
  required?: boolean;
}

export function NumericTextField({
  id,
  label,
  suffix,
  decimalSeparator,
  decimalScale,
  maxLength,
  value,
  setValue,
  disable,
  error,
  required,
}: NumericTextFieldProps): JSX.Element {
  return (
    <NumericFormat
      customInput={TextField}
      disabled={disable || false}
      error={error || false}
      id={id}
      label={label}
      suffix={suffix}
      decimalSeparator={decimalSeparator || '.'}
      decimalScale={decimalScale || 0}
      allowNegative={false}
      inputProps={{ maxLength }}
      value={value}
      onChange={(e) => {
        const convertedValue = convertToFloatNumber(e.target.value);
        setValue(convertedValue || 0);
      }}
      sx={textFieldDefaultStyle}
      required={required}
    />
  );
}

interface PreffixNumericTextFieldProps {
  required?: boolean;
  id: string;
  label: ReactNode;
  prefix: string;
  decimalSeparator?: string;
  decimalScale?: number;
  maxLength: number;
  value: number;
  setValue: (value: number) => void;
  disabled?: boolean;
}

export function PreffixNumericTextField({
  required,
  id,
  label,
  prefix,
  decimalSeparator,
  decimalScale,
  maxLength,
  value,
  setValue,
  disabled,
}: PreffixNumericTextFieldProps): JSX.Element {
  return (
    <NumericFormat
      required={required}
      disabled={disabled || false}
      customInput={TextField}
      id={id}
      label={label}
      color="secondary"
      prefix={prefix}
      decimalSeparator={decimalSeparator || '.'}
      decimalScale={decimalScale || 0}
      thousandsGroupStyle="thousand"
      thousandSeparator=" "
      allowNegative={false}
      inputProps={{ maxLength }}
      value={value}
      onChange={(e) => {
        const convertedValue = convertToFloatNumber(e.target.value);
        setValue(convertedValue);
      }}
      sx={textFieldDefaultStyle}
    />
  );
}

interface CustomFormControlProps {
  label: string;
  isChecked: boolean;
  setIsChecked: (isChecked: boolean) => void;
}

export function CustomFormControl({
  label,
  isChecked,
  setIsChecked,
}: CustomFormControlProps): JSX.Element {
  return (
    <FormControlLabel
      label={label}
      control={
        <Checkbox
          checked={isChecked}
          onChange={(event) => {
            setIsChecked(event.target.checked);
          }}
        />
      }
      sx={(theme) => ({
        '&:hover': {
          '& .MuiTypography-root': {
            fontWeight: '600',
            color: theme.palette.common.grey,
          },
        },
        '& .MuiButtonBase-root': {
          color: theme.palette.common.grey,
          '&.Mui-checked': {
            color: theme.palette.common.grey,
          },
        },
        '& .MuiTypography-root': {
          '&.Mui-disabled': {
            color: '#737373',
          },
        },
      })}
    />
  );
}

interface PathologiesFormControlProps {
  label: string;
  value: number;
  isChecked: boolean;
  onChangeCallback: (checked: boolean, value: number) => void;
  disabled: boolean;
}

export function PathologiesFormControl({
  label,
  value,
  isChecked,
  onChangeCallback,
  disabled,
}: PathologiesFormControlProps): JSX.Element {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  return (
    <FormControlLabel
      control={
        <Checkbox
          disabled={disabled}
          checked={checked}
          onChange={(event) => {
            setChecked(event.target.checked);
            onChangeCallback(event.target.checked, value);
          }}
        />
      }
      sx={(theme) => ({
        '&:hover': {
          '& .MuiTypography-root': {
            fontWeight: '600',
          },
        },
        '& .MuiButtonBase-root': {
          color: theme.palette.common.grey,
          '&.Mui-disabled': {
            color: alpha(theme.palette.common.grey, 0.3),
          },
        },
        '& .MuiTypography-root': {
          '&.Mui-disabled': {
            color: '#737373',
          },
        },
      })}
      label={label}
    />
  );
}

interface StyledSelectProps {
  id: string;
  label: string;
  value: string;
  setValue: (value: string) => void;
  selectOptions: string[];
  disabled?: boolean;
  required?: boolean;
}

export function StyledSelect({
  id,
  label,
  value,
  setValue,
  selectOptions,
  disabled,
  required,
}: StyledSelectProps): JSX.Element {
  return (
    <TextField
      disabled={disabled}
      required={required}
      id={id}
      select
      label={label}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      sx={textFieldDefaultStyle}
    >
      <MenuItem disabled value="" sx={{ color: '#7c7c7c' }}>
        <Typography>selecione uma opção</Typography>
      </MenuItem>
      {selectOptions.map((option) => (
        <MenuItem
          key={option}
          value={option}
          sx={(theme) => ({
            '&:hover': {
              backgroundColor: '#EDEDED',
            },
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.common.grey, 0.3),
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.grey, 0.3),
              },
            },
          })}
        >
          {option}
        </MenuItem>
      ))}
    </TextField>
  );
}

interface ReportNumericTextFieldProps {
  id: string;
  label: string;
  value: number | string | null;
  disable?: boolean;
}

export function ReportNumericTextField({
  id,
  label,
  value,
  disable,
}: ReportNumericTextFieldProps): JSX.Element {
  return (
    <NumericFormat
      customInput={TextField}
      id={id}
      label={label}
      color="secondary"
      prefix="R$ "
      decimalSeparator=","
      decimalScale={2}
      thousandsGroupStyle="thousand"
      thousandSeparator=" "
      allowNegative={false}
      inputProps={{ maxLength: 20 }}
      value={value}
      disabled={disable}
      sx={textFieldDefaultStyle}
    />
  );
}

interface DocumentPatternFormatProps extends PatternFormatProps {
  id: string;
  label: string;
  value: string;
  setValue?: (value: string) => void;
  minLength?: number;
  format: string;
}

export function DocumentPatternFormat({
  id,
  label,
  value,
  setValue,
  minLength,
  format,
}: DocumentPatternFormatProps): JSX.Element {
  const [error, setError] = useState(false);
  const handleInvalid = (event: ChangeEvent<HTMLInputElement>): void =>
    event.target.setCustomValidity('Preencha este campo');

  const handleInput = (event: ChangeEvent<HTMLInputElement>): void =>
    event.target.setCustomValidity('');

  return (
    <PatternFormat
      id={id}
      customInput={TextField}
      label={label}
      required
      format={format}
      variant="standard"
      color="secondary"
      mask="_"
      value={value}
      onInvalid={handleInvalid}
      onInput={handleInput}
      error={value.length > 0 && error}
      onChange={(e) => {
        if (setValue) setValue(e.target.value);
        if (!!minLength && cleanValue(e.target.value).length < minLength) {
          setError(true);
        } else {
          setError(false);
        }
      }}
      sx={textFieldDefaultStyle}
    />
  );
}
